import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api';

const CATEGORIES = {
  'getting-started': { label: 'Getting Started', icon: '🚀' },
  'resources': { label: 'Resources & Economy', icon: '⛏' },
  'fleet': { label: 'Ships & Fleet', icon: '▲' },
  'stations': { label: 'Stations', icon: '⬡' },
  'combat': { label: 'Combat', icon: '⚔' },
  'sectors': { label: 'Sectors & Exploration', icon: '◇' },
  'production': { label: 'Production & Crafting', icon: '⚙' },
  'market': { label: 'Market & Trade', icon: '◈' },
  'corporations': { label: 'Corporations', icon: '⛨' },
  'general': { label: 'General', icon: '📖' },
};

function GuidePanel() {
  const [articles, setArticles] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getGuideArticles();
      setArticles(data.articles || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  useEffect(() => {
    if (!selectedSlug) { setArticle(null); return; }
    (async () => {
      try {
        const { data } = await api.getGuideArticle(selectedSlug);
        setArticle(data.article);
      } catch { setArticle(null); }
    })();
  }, [selectedSlug]);

  // Group by category
  const grouped = {};
  for (const a of articles) {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  }

  if (loading) return <div className="panel"><p className="hud-empty">Loading guide...</p></div>;

  return (
    <div className="panel guide-panel">
      <section className="section">
        <h3 className="section-title"><span className="title-accent">📖</span> GAME GUIDE</h3>
        {articles.length === 0 && (
          <p className="hud-empty">No guide articles yet. The game master will add content here.</p>
        )}
      </section>

      <div className="guide-layout">
        {/* Sidebar: article list grouped by category */}
        <aside className="guide-sidebar">
          {Object.entries(grouped).map(([cat, arts]) => {
            const catInfo = CATEGORIES[cat] || { label: cat, icon: '📄' };
            return (
              <div key={cat} className="guide-category">
                <div className="guide-cat-header">
                  <span className="guide-cat-icon">{catInfo.icon}</span>
                  <span className="guide-cat-label">{catInfo.label}</span>
                </div>
                <div className="guide-cat-articles">
                  {arts.map(a => (
                    <button
                      key={a.id}
                      className={`guide-article-btn ${selectedSlug === a.slug ? 'active' : ''}`}
                      onClick={() => setSelectedSlug(a.slug)}
                    >
                      {a.title}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </aside>

        {/* Article content */}
        <div className="guide-content">
          {article ? (
            <section className="section">
              <h3 className="section-title">
                <span className="title-accent">{CATEGORIES[article.category]?.icon || '📄'}</span>
                {article.title}
              </h3>
              <div className="guide-article-body" dangerouslySetInnerHTML={{ __html: renderContent(article.content) }} />
            </section>
          ) : (
            <section className="section">
              <p className="hud-empty">Select an article from the sidebar to read it.</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Simple markdown-ish to HTML renderer.
 * Supports: # headings, **bold**, *italic*, - lists, blank line = paragraphs, `code`
 */
function renderContent(text) {
  if (!text) return '';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped
    .replace(/^### (.+)$/gm, '<h5 class="guide-h3">$1</h5>')
    .replace(/^## (.+)$/gm, '<h4 class="guide-h2">$1</h4>')
    .replace(/^# (.+)$/gm, '<h3 class="guide-h1">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="guide-code">$1</code>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul class="guide-list">${m}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');
}

export default GuidePanel;
